import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { sizeFromHours, SIZE_LABELS } from '@rundown/domain';
import { cx } from '../../lib/cx';
import { formatDate, formatHours } from '../../lib/format';
import { TYPE_LABELS, TYPE_TONES } from '../requests/labels';
import { useSizeThresholds, useVendorName } from '../workspace/useWorkspace';
import { requestQueryOptions } from '../requests/api';
import { PendingStrip } from '../attachments/AttachmentStrip';
import { useUploader } from '../attachments/useUploader';
import { answerIntake, submitIntakeAnswers, type AnswerInput } from './api';
import { clearDraft } from './draftStorage';
import { intakeQueryKeys } from './useIntakeState';
import type { DuplicateChoice, IntakeProposal, IntakeQuestion, IntakeState } from './types';

/**
 * The conversation itself — the duplicate check, and the round's questions
 * with their answers.
 *
 * Extracted from IntakePanel because it has TWO homes, not one. A client who
 * is impatient (or who just closed the tab) can open the request directly
 * while intake is still thinking, and if the questions only ever existed on
 * the Home screen they would arrive somewhere that person is no longer
 * looking. The same conversation now renders in both places, reading the
 * same server state, so answering it on the request page and answering it in
 * the ask box are the same act.
 *
 * ── ANSWERS ARE LOCAL UNTIL THE BUTTON ───────────────────────────────────
 * Every tap used to POST, and the tap that resolved the last open question
 * bought a full model round — so a three-question round cost three requests,
 * three spinners, and a re-analysis the client never asked for. Now the
 * picks live in component state and go up together, once. Tapping is
 * instant because nothing leaves the page, and the round costs exactly one
 * model call, computed WITH the answers rather than before them.
 *
 * The one exception is the duplicate check, which still acts on the tap:
 * "Same thing" sends the client's words to a different request and navigates
 * there, so it cannot wait in a buffer behind a button.
 *
 * ── NOT ANSWERING IS THE SKIP ────────────────────────────────────────────
 * There is no per-question "Skip this" any more. Leaving a question blank
 * already means "I'm not answering that", and the server records exactly
 * that. The old button advertised the escape beside every single question,
 * which read as encouragement to take it — there is still one way out of
 * the whole round ("Skip and post"), and it is the only one that needs
 * saying out loud.
 */

const CHIP_BASE = 'rounded-full border px-3 py-1.5 fs-12 font-medium disabled:opacity-50';
const CHIP_IDLE = 'border-line-strong text-ink-soft hover:border-line-heavy hover:bg-surface';
const CHIP_ON = 'border-accent bg-accent-tint text-accent-strong';

const FIELD_CLASS =
  'w-full rounded-lg border border-line-strong bg-transparent px-2.5 py-1.5 fs-12 placeholder:text-faint focus:border-accent focus:outline-none disabled:opacity-50';

/** Local duplicate of review/ReviewList's map — see that file for why each is its own copy. */
const TONE_CHIP: Record<string, string> = {
  neutral: 'bg-surface-3 text-ink-soft',
  accent: 'bg-accent-tint text-accent',
  success: 'bg-success-tint text-success',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  violet: 'bg-violet-tint text-violet',
};

const DUPLICATE_CHOICES: ReadonlyArray<{ value: DuplicateChoice; label: string }> = [
  { value: 'same_thing', label: 'Same thing' },
  { value: 'related_but_different', label: 'Related but different' },
  { value: 'new_issue', label: 'New issue' },
];

/**
 * What the AI made of the request, with its classification and size.
 *
 * Shown ONLY once intake has settled. While questions are still open this
 * would be a type badge and an hour figure derived from a single sentence,
 * sitting directly above the questions asked because that sentence was not
 * enough — which is both confusing to read and wrong to publish: the
 * estimate a client remembers should be the one computed with their answers,
 * not the guess that preceded them.
 */
export function ProposalSummary({ proposal }: { readonly proposal: IntakeProposal }) {
  const sizeThresholds = useSizeThresholds();
  const vendorName = useVendorName();
  const size = sizeFromHours(proposal.approxMinutes / 60, sizeThresholds);
  return (
    <div>
      {/* clientTitle is the ONLY title this screen may ever show — the API
          never sends the internal one to a client, and there is nothing else
          here that could stand in for it. */}
      <p className="fs-13 font-semibold text-ink">{proposal.clientTitle}</p>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <span
          className={cx(
            'rounded-full px-2 py-0.5 fs-11 font-bold',
            TONE_CHIP[TYPE_TONES[proposal.type]] ?? TONE_CHIP['neutral'],
          )}
        >
          {TYPE_LABELS[proposal.type]}
        </span>
        {proposal.projectName !== null && (
          <span className="rounded-full bg-surface-3 px-2 py-0.5 fs-11 font-semibold text-ink-soft">
            {proposal.projectName}
          </span>
        )}
        <span className="rounded-full bg-surface-3 px-2 py-0.5 fs-11 font-semibold text-ink-soft">
          {SIZE_LABELS[size]} · ~{formatHours(proposal.approxMinutes / 60)}
        </span>
      </div>
      {/* Never a quote: v0.3 §6.1 is explicit that a client cannot be shown a
          number that reads as a commitment. */}
      <p className="mt-1.5 fs-11 text-faint">
        Indicative only, not a quote or a delivery date — {vendorName} confirms the details.
      </p>
      {proposal.summary !== '' && <p className="mt-1.5 fs-12 text-muted">{proposal.summary}</p>}
    </div>
  );
}

function DuplicatePrompt({
  workspaceId,
  requestId,
  duplicateRequestId,
  number,
  title,
  question,
  busy,
  onChoose,
}: {
  readonly workspaceId: string;
  readonly requestId: string;
  readonly duplicateRequestId: string;
  readonly number: number;
  readonly title: string;
  readonly question: string;
  readonly busy: boolean;
  readonly onChoose: (choice: DuplicateChoice) => void;
}) {
  // The date the earlier request was raised — "is this the same thing Jamie
  // reported on 12 Sep" is a much easier question than the same one without a
  // date. It is already visible to this client (a duplicate candidate is by
  // construction a request they can see); the buttons never wait on it.
  const existing = useQuery({
    ...requestQueryOptions(workspaceId, duplicateRequestId),
    enabled: duplicateRequestId !== requestId,
  });

  return (
    <div className="rounded-xl border border-warn-border bg-warn-tint p-3.5">
      <p className="fs-12 font-semibold text-ink">Possibly the same as an existing request</p>
      <p className="mt-1 fs-12 text-ink-soft">
        #{number} · {title}
        {existing.data !== undefined && ` · ${formatDate(existing.data.createdAt)}`}
      </p>
      <p className="mt-1.5 fs-12 text-muted">{question}</p>
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {DUPLICATE_CHOICES.map((choice) => (
          <button
            key={choice.value}
            type="button"
            disabled={busy}
            onClick={() => onChoose(choice.value)}
            className={cx(CHIP_BASE, 'bg-base', CHIP_IDLE)}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The attach control an `evidence` question renders, owned by the conversation. */
interface EvidenceControl {
  readonly uploads: ReturnType<typeof useUploader>['uploads'];
  readonly isUploading: boolean;
  readonly addFiles: (files: readonly File[]) => void;
  readonly removeUpload: (localId: string) => void;
}

interface QuestionCardProps {
  readonly question: IntakeQuestion;
  readonly position: number;
  readonly busy: boolean;
  readonly picked: ReadonlySet<string>;
  readonly note: string;
  readonly otherOpen: boolean;
  readonly evidence: EvidenceControl | null;
  readonly onToggle: (value: string) => void;
  readonly onOpenOther: () => void;
  readonly onNoteChange: (value: string) => void;
}

/**
 * One question. Pure — it sends nothing anywhere; the parent holds the
 * answers and submits them together.
 *
 * EVERY choice question is multi-select, whatever `kind` says. The cost of
 * the two possible mistakes is not symmetric: a multi that only ever had one
 * real answer is answered with one tap and nobody notices, while a single
 * that should have allowed two makes the client under-report the problem —
 * asked "what looks inconsistent: colours, layout, buttons?" they very often
 * mean two of them, and a radio group silently throws one away. Nothing
 * downstream is typed by this either: the answer is folded into the
 * description as prose ("A: Colours, Layout"), never into a column. So the
 * permissive reading is always safe and sometimes necessary.
 */
function QuestionCard({
  question,
  position,
  busy,
  picked,
  note,
  otherOpen,
  evidence,
  onToggle,
  onOpenOther,
  onNoteChange,
}: QuestionCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isChoice = question.kind === 'single' || question.kind === 'multi';
  // A choice question shows its written escape only once asked for, so the
  // quick answers stay the obvious thing to do — but it is always one tap
  // away, because no closed list covers every real answer.
  const showNoteField = question.kind === 'text' || (isChoice && otherOpen);

  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-surface-3 fs-11 font-bold text-muted">
        {position}
      </span>
      <div className="min-w-0 flex-1">
        <p className="fs-12 font-semibold text-ink">{question.prompt}</p>

        {isChoice && (
          <>
            <p className="mt-0.5 fs-11 text-faint">Pick any that apply</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={busy}
                  aria-pressed={picked.has(option.value)}
                  onClick={() => onToggle(option.value)}
                  className={cx(CHIP_BASE, picked.has(option.value) ? CHIP_ON : CHIP_IDLE)}
                >
                  {option.label}
                </button>
              ))}
              {question.allowOther && !otherOpen && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={onOpenOther}
                  className={cx(CHIP_BASE, 'border-dashed', CHIP_IDLE)}
                >
                  Something else…
                </button>
              )}
            </div>
          </>
        )}

        {showNoteField && (
          <input
            value={note}
            autoFocus={otherOpen}
            onChange={(event) => onNoteChange(event.target.value)}
            disabled={busy}
            placeholder={question.kind === 'text' ? 'Type your answer…' : 'Tell us in your own words…'}
            className={cx(FIELD_CLASS, 'mt-2')}
          />
        )}

        {question.kind === 'evidence' && evidence !== null && (
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(event) => {
                evidence.addFiles([...(event.target.files ?? [])]);
                // Reset, or picking the same file twice in a row fires nothing.
                event.target.value = '';
              }}
            />
            {/* A link is a first-class answer here, not a consolation prize.
                Clients record a Loom far more readily than they save a file
                and upload it, and a link to one settles the question just as
                well. It doubles as the paste target: this input is what
                "paste it here" was always promising and never had — the old
                copy said it beside a plain <div>, which cannot receive a
                paste at all, so pasting there did nothing. Images pasted
                anywhere on the panel are picked up separately; see the
                window listener in IntakeConversation. */}
            <input
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
              disabled={busy}
              placeholder="Paste a link — Loom, a video, a shared file…"
              className={FIELD_CLASS}
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
                className={cx(CHIP_BASE, CHIP_IDLE, 'inline-flex items-center gap-1.5')}
              >
                <Paperclip size={13} strokeWidth={1.75} aria-hidden />
                Add a screenshot or recording
              </button>
              <span className="fs-11 text-faint">or paste an image anywhere here</span>
            </div>
            <PendingStrip uploads={evidence.uploads} onRemove={evidence.removeUpload} />
          </div>
        )}
      </div>
    </div>
  );
}

export interface IntakeConversationProps {
  readonly workspaceId: string;
  readonly requestId: string;
  readonly state: IntakeState;
  /**
   * The one-line "here's what we understood", shown in the ask box where the
   * client has nothing else on screen to confirm it against. False on the
   * request screen, where the request itself is right there.
   */
  readonly showSummary: boolean;
  /** Rendered beside the submit button — the single way out of the round. */
  readonly escape: React.ReactNode;
}

/**
 * The answerable part of a round. Renders nothing at all when there is
 * nothing to answer, so a caller can mount it unconditionally.
 */
export function IntakeConversation({
  workspaceId,
  requestId,
  state,
  showSummary,
  escape,
}: IntakeConversationProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [picks, setPicks] = useState<Record<string, ReadonlySet<string>>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [otherOpen, setOtherOpen] = useState<Record<string, boolean>>({});
  const [duplicatePending, setDuplicatePending] = useState(false);

  // One uploader for the whole conversation rather than one per question: the
  // server is told to ask for evidence sparingly and only when a picture
  // beats several rounds of words, so two open evidence questions at once is
  // not a shape it produces. Files register against the request immediately —
  // it already exists — so they are safe before the answer is sent.
  const uploader = useUploader(workspaceId, { requestId });
  const hasEvidenceQuestion = state.questions.some((question) => question.kind === 'evidence');

  /**
   * Pasting a screenshot, from anywhere on the page.
   *
   * The old code hung `onPaste` on a plain `<div>`, which never fires: a div
   * is not focusable and never becomes the paste target, so "or paste one
   * here" pointed at nothing. A window listener is what actually catches it,
   * and it is safe to listen that widely because it only ever consumes a
   * paste carrying FILES — pasting text into the link field, or into any
   * other input on the page, has none and falls straight through.
   */
  const addFiles = uploader.addFiles;
  useEffect(() => {
    if (!hasEvidenceQuestion) return;

    const onPaste = (event: ClipboardEvent) => {
      const files = [...(event.clipboardData?.files ?? [])];
      if (files.length === 0) return;
      event.preventDefault();
      void addFiles(files);
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [hasEvidenceQuestion, addFiles]);

  const writeState = (next: IntakeState) =>
    queryClient.setQueryData<IntakeState>(intakeQueryKeys.intake(workspaceId, requestId), next);

  const send = useMutation({
    mutationFn: (answers: readonly AnswerInput[]) => submitIntakeAnswers(workspaceId, requestId, answers),
    onSuccess: writeState,
  });
  // The duplicate check answers on the tap — see the module doc.
  const answerDuplicate = useMutation({
    mutationFn: (input: AnswerInput) => answerIntake(workspaceId, requestId, input),
    onSuccess: writeState,
  });

  const busy = send.isPending || answerDuplicate.isPending || duplicatePending;
  const error = send.error ?? answerDuplicate.error;

  /**
   * Everything the client has actually filled in, in the order the questions
   * were asked. A question with nothing against it is simply absent, which
   * is how the server is told it was skipped.
   */
  const answers = useMemo<readonly AnswerInput[]>(() => {
    const out: AnswerInput[] = [];
    for (const question of state.questions) {
      const note = (notes[question.id] ?? '').trim();
      const selected = [...(picks[question.id] ?? [])];

      if (question.kind === 'evidence') {
        const attachmentIds = uploader.attachmentIds;
        if (attachmentIds.length === 0 && note === '') continue;
        out.push({
          questionId: question.id,
          attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
          text: note === '' ? undefined : note,
        });
        continue;
      }

      if (selected.length === 0 && note === '') continue;
      out.push({
        questionId: question.id,
        selected: selected.length > 0 ? selected : undefined,
        text: note === '' ? undefined : note,
      });
    }
    return out;
  }, [state.questions, notes, picks, uploader.attachmentIds]);

  function toggle(question: IntakeQuestion, value: string) {
    setPicks((current) => {
      const set = new Set(current[question.id] ?? []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...current, [question.id]: set };
    });
  }

  async function chooseDuplicate(duplicateRequestId: string, choice: DuplicateChoice) {
    setDuplicatePending(true);
    try {
      // The duplicate confirmation has no id of its own in IntakeState — it
      // is answered through the same endpoint under a fixed sentinel id.
      await answerDuplicate.mutateAsync({ questionId: 'duplicate', selected: [choice] });
      // "Same thing" means the client's words belong on the EARLIER thread —
      // send them there, not to the request this panel is attached to.
      if (choice === 'same_thing') {
        clearDraft(workspaceId);
        void navigate(`/requests/${duplicateRequestId}`);
      }
    } finally {
      setDuplicatePending(false);
    }
  }

  const evidenceControl: EvidenceControl = {
    uploads: uploader.uploads,
    isUploading: uploader.isUploading,
    addFiles: (files) => void uploader.addFiles(files),
    removeUpload: uploader.remove,
  };

  return (
    <>
      {showSummary && state.proposal !== null && state.proposal.summary !== '' && (
        <p className="fs-12 text-ink-soft">{state.proposal.summary}</p>
      )}

      {state.duplicate !== null && (
        <div className={showSummary && state.proposal !== null ? 'mt-3' : ''}>
          <DuplicatePrompt
            workspaceId={workspaceId}
            requestId={requestId}
            duplicateRequestId={state.duplicate.requestId}
            number={state.duplicate.number}
            title={state.duplicate.title}
            question={state.duplicate.question}
            busy={busy}
            onChoose={(choice) => {
              // Captured as a local const so the closure keeps the narrowed
              // (non-null) type rather than re-reading `state.duplicate`,
              // which TS cannot prove is still non-null once control leaves
              // this expression.
              const duplicateRequestId = state.duplicate?.requestId;
              if (duplicateRequestId !== undefined) void chooseDuplicate(duplicateRequestId, choice);
            }}
          />
        </div>
      )}

      {state.questions.length > 0 && (
        <div className={cx('space-y-3.5', state.duplicate !== null || showSummary ? 'mt-3' : '')}>
          {state.questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              question={question}
              position={index + 1}
              busy={busy}
              picked={picks[question.id] ?? new Set<string>()}
              note={notes[question.id] ?? ''}
              otherOpen={otherOpen[question.id] === true}
              evidence={question.kind === 'evidence' ? evidenceControl : null}
              onToggle={(value) => toggle(question, value)}
              onOpenOther={() => setOtherOpen((current) => ({ ...current, [question.id]: true }))}
              onNoteChange={(value) => setNotes((current) => ({ ...current, [question.id]: value }))}
            />
          ))}
        </div>
      )}

      {state.questions.length > 0 && (
        <div className="mt-3.5 flex flex-wrap items-center gap-3 border-t border-line pt-3">
          <button
            type="button"
            onClick={() => send.mutate(answers)}
            disabled={busy || answers.length === 0 || uploader.isUploading}
            className="rounded-lg bg-ink px-3.5 py-2 fs-12 font-semibold text-white disabled:opacity-40"
          >
            {send.isPending ? 'Sending…' : uploader.isUploading ? 'Uploading…' : 'Send answers'}
          </button>
          {escape}
        </div>
      )}

      {error !== null && (
        <p role="alert" className="mt-2 fs-11 text-danger">
          That didn't go through. {error.message}
        </p>
      )}
    </>
  );
}
